import * as React from "react";
import { toast, Autocomplete } from "@grandvisionhq/common";
import { Icon } from "@grandvisionhq/icons";
import { useIntl } from "@grandvisionhq/state";
import { useDebouncedEffect } from "@grandvisionhq/utils";
import usePlacePredictions from "../../../../../../hooks/api/hooks/api/use-place-predictions.js";
import { usePlaceDetails } from "../../../../../../hooks/api/hooks/api/use-place-details.js";
import { mapPlacePredictionToSuggestion } from "../../../../../../mappers/mappers/map-place-prediction-to-suggestion.js";
const getPlaceDetailsWithStreetName = (placeDetails, searchQuery) => {
  if (!placeDetails.streetName) {
    return { ...placeDetails, streetName: searchQuery };
  }
  return placeDetails;
};
const MIN_SEARCH_CHARACTER_COUNT = 2;
const AddressSearchFormContainer = ({
  handleClickOutside,
  inputFieldName,
  isInvalid,
  isValid,
  minSearchCharacterCount = MIN_SEARCH_CHARACTER_COUNT,
  onResult,
  setIsTouched,
  streetName,
  updateForm
}) => {
  const [searchQuery, setSearchQuery] = React.useState(streetName);
  const [suggestions, setSuggestions] = React.useState([]);
  const [isSuggestionSelected, setIsSuggestionSelected] = React.useState("");
  const { getLabel } = useIntl();
  const [getPlacePredictions, placePredictions] = usePlacePredictions({
    variables: { name: searchQuery },
    errorPolicy: "all",
    fetchPolicy: "no-cache"
  });
  const [getPlaceDetails, placeDetails] = usePlaceDetails();
  const labels = React.useMemo(
    () => ({
      placeholder: getLabel("locations.addressAutoComplete.placeholder"),
      noResults: getLabel("locations.addressAutoComplete.noResults")
    }),
    [getLabel]
  );
  const handleOnChange = React.useCallback(
    (value) => {
      setIsSuggestionSelected("");
      setSearchQuery(value);
      updateForm(value);
    },
    [updateForm]
  );
  React.useEffect(() => {
    setIsSuggestionSelected("default");
  }, []);
  const onChange = React.useCallback(
    (event) => handleOnChange(event.currentTarget.value),
    [handleOnChange]
  );
  const handleOnSearch = React.useCallback(() => {
    if (searchQuery.length >= minSearchCharacterCount && isSuggestionSelected !== placeDetails.data?.id) {
      getPlacePredictions({ variables: { name: searchQuery } });
    }
  }, [
    searchQuery,
    minSearchCharacterCount,
    isSuggestionSelected,
    placeDetails.data?.id,
    getPlacePredictions
  ]);
  useDebouncedEffect(
    () => {
      handleOnSearch();
    },
    500,
    [searchQuery]
  );
  const onSubmit = React.useCallback(() => {
    suggestions.length > 0 && getPlaceDetails({
      variables: {
        id: suggestions[0]?.id ?? ""
      }
    });
  }, [getPlaceDetails, suggestions]);
  const onSuggestionClick = React.useCallback(
    (id) => {
      setSuggestions([]);
      setIsSuggestionSelected(id);
      id && getPlaceDetails({
        variables: {
          id
        }
      });
    },
    [getPlaceDetails]
  );
  const onClear = React.useCallback(() => {
    setSearchQuery("");
    setSuggestions([]);
    setIsSuggestionSelected("");
    updateForm("");
  }, [updateForm]);
  React.useEffect(() => {
    streetName && setSearchQuery(streetName);
  }, [streetName]);
  React.useEffect(() => {
    if (!placeDetails.data) {
      return;
    }
    const placeDetailsWithStreetName = getPlaceDetailsWithStreetName(placeDetails.data, searchQuery);
    placeDetails.data && onResult(placeDetailsWithStreetName);
  }, [placeDetails.data, onResult]);
  React.useEffect(() => {
    if (placePredictions.data) {
      setSuggestions(mapPlacePredictionToSuggestion(placePredictions.data));
    }
  }, [placePredictions]);
  React.useEffect(() => {
    if (placePredictions.error) {
      toast.failed("locations.getPlacePredictions.error");
      console.error("Failed to fetch place predictions: ", placePredictions.error);
    }
    if (placeDetails.error) {
      toast.failed("locations.getPlaceDetails.error");
      console.error("Failed to fetch place details: ", placeDetails.error);
    }
  }, [placePredictions.error, placeDetails.error]);
  return /* @__PURE__ */ React.createElement(
    Autocomplete,
    {
      handleClickOutside,
      isInvalid,
      isSuggestionSelected: Boolean(isSuggestionSelected),
      isValid,
      labels,
      loading: placePredictions.loading || placeDetails.loading,
      minCountCharactersToSubmit: minSearchCharacterCount,
      name: inputFieldName,
      onChange,
      onClear,
      onSubmit,
      onSuggestionClick,
      setIsTouched,
      showClearButton: Boolean(searchQuery),
      submitIcon: /* @__PURE__ */ React.createElement(Icon, { name: "search" }),
      suggestions,
      type: "search",
      value: streetName
    }
  );
};
export {
  AddressSearchFormContainer
};
//# sourceMappingURL=AddressSearchFormContainer.js.map
